import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
const TOKEN_LABELS = {
  termsOfUse: "Terms of Use",
  privacyPolicy: "Privacy Policy",
  consumerHealth: "Consumer Health Data Privacy Policy",
  contactEmail: ""
};
const TOKEN_PATTERN = /\{(termsOfUse|privacyPolicy|consumerHealth|contactEmail)\}/g;
function getTokenHref(token, agreement) {
  switch (token) {
    case "termsOfUse":
      return agreement.termsOfUseUrl || null;
    case "privacyPolicy":
      return agreement.privacyPolicyUrl || null;
    case "consumerHealth":
      return agreement.consumerHealthUrl || null;
    case "contactEmail":
      return agreement.contactEmail ? `mailto:${agreement.contactEmail}` : null;
    default:
      return null;
  }
}
function getTokenLabel(token, agreement) {
  if (token === "contactEmail") return agreement.contactEmail || `{${token}}`;
  return TOKEN_LABELS[token] || `{${token}}`;
}
function renderBody(body, agreement, linkColor) {
  const nodes = [];
  let cursor = 0;
  let match;
  let key = 0;
  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(body)) !== null) {
    if (match.index > cursor) {
      nodes.push(body.slice(cursor, match.index));
    }
    const token = match[1];
    const href = getTokenHref(token, agreement);
    const label = getTokenLabel(token, agreement);
    if (href) {
      const isMail = href.startsWith("mailto:");
      nodes.push(
        /* @__PURE__ */ jsx(
          "a",
          {
            href,
            target: isMail ? void 0 : "_blank",
            rel: isMail ? void 0 : "noopener noreferrer",
            style: { color: linkColor, textDecoration: "underline" },
            children: label
          },
          `tok-${key++}`
        )
      );
    } else {
      nodes.push(match[0]);
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < body.length) nodes.push(body.slice(cursor));
  return nodes;
}
function renderParagraphs(body, agreement, textColor, linkColor) {
  const paragraphs = body.split(/\n\s*\n/);
  return paragraphs.map((para, i) => /* @__PURE__ */ jsx(
    "p",
    {
      style: {
        margin: "0 0 12px",
        fontSize: 15,
        lineHeight: "22px",
        color: textColor,
        whiteSpace: "pre-wrap"
      },
      children: renderBody(para, agreement, linkColor)
    },
    `p-${i}`
  ));
}
const AgreeButton = ({
  buttonLabel,
  primaryColor,
  buttonTextColor,
  onAgree
}) => /* @__PURE__ */ jsx(
  "button",
  {
    type: "button",
    onClick: onAgree,
    style: {
      width: "100%",
      padding: "14px 16px",
      background: primaryColor,
      color: buttonTextColor,
      border: "none",
      borderRadius: 4,
      fontSize: 15,
      fontWeight: 500,
      cursor: "pointer"
    },
    children: buttonLabel
  }
);
const PendingPromptBubble = ({
  text,
  primaryColor,
  buttonTextColor
}) => /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "flex-end" }, children: /* @__PURE__ */ jsx(
  "div",
  {
    style: {
      maxWidth: "80%",
      background: primaryColor,
      color: buttonTextColor,
      borderRadius: 20,
      padding: "10px 16px",
      fontSize: 15,
      lineHeight: "22px",
      wordBreak: "break-word"
    },
    children: text
  }
) });
const InlineLayout = ({
  agreement,
  pendingUserPrompt,
  buttonLabel,
  textColor,
  primaryColor,
  buttonTextColor,
  linkColor,
  onAgree
}) => {
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || "", agreement, textColor, linkColor),
    [agreement, textColor, linkColor]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "region",
      "aria-label": agreement.title || "Agreement",
      style: {
        position: "absolute",
        inset: 0,
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        background: "rgba(255,255,255,0.96)"
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            padding: "24px 20px 20px",
            maxWidth: 720,
            width: "100%",
            margin: "0 auto",
            maxHeight: "100%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 16
          },
          children: [
            pendingUserPrompt ? /* @__PURE__ */ jsx(PendingPromptBubble, { text: pendingUserPrompt, primaryColor, buttonTextColor }) : null,
            /* @__PURE__ */ jsxs("div", { children: [
              agreement.title ? /* @__PURE__ */ jsx(
                "p",
                {
                  style: {
                    margin: "0 0 8px",
                    fontSize: 15,
                    lineHeight: "22px",
                    fontWeight: 600,
                    color: textColor
                  },
                  children: agreement.title
                }
              ) : null,
              rendered
            ] }),
            /* @__PURE__ */ jsx(
              AgreeButton,
              {
                buttonLabel,
                primaryColor,
                buttonTextColor,
                onAgree
              }
            )
          ]
        }
      )
    }
  );
};
const CenteredModalLayout = ({
  agreement,
  buttonLabel,
  textColor,
  primaryColor,
  buttonTextColor,
  linkColor,
  onAgree
}) => {
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || "", agreement, textColor, linkColor),
    [agreement, textColor, linkColor]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "dialog",
      "aria-modal": "true",
      "aria-label": agreement.title || "Agreement",
      style: {
        position: "absolute",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.4)",
        padding: 16
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            background: "#fff",
            borderRadius: 12,
            padding: 24,
            maxWidth: 480,
            width: "100%",
            maxHeight: "100%",
            overflowY: "auto",
            boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: 16
          },
          children: [
            agreement.title ? /* @__PURE__ */ jsx(
              "h2",
              {
                style: {
                  margin: 0,
                  fontSize: 18,
                  lineHeight: "24px",
                  fontWeight: 700,
                  color: textColor,
                  textAlign: "center"
                },
                children: agreement.title
              }
            ) : null,
            /* @__PURE__ */ jsx("div", { children: rendered }),
            /* @__PURE__ */ jsx(
              AgreeButton,
              {
                buttonLabel,
                primaryColor,
                buttonTextColor,
                onAgree
              }
            )
          ]
        }
      )
    }
  );
};
const BottomBannerLayout = ({
  agreement,
  buttonLabel,
  textColor,
  primaryColor,
  buttonTextColor,
  linkColor,
  onAgree
}) => {
  const [expanded, setExpanded] = useState(false);
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || "", agreement, textColor, linkColor),
    [agreement, textColor, linkColor]
  );
  const headline = agreement.title || "Please review and accept to continue.";
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "region",
      "aria-label": agreement.title || "Agreement",
      style: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        background: "#fff",
        borderTop: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
        padding: "12px 16px",
        maxHeight: "60%",
        overflowY: "auto"
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            maxWidth: 720,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 8
          },
          children: [
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 12 }, children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    flex: 1,
                    fontSize: 14,
                    lineHeight: "20px",
                    color: textColor,
                    fontWeight: 600
                  },
                  children: headline
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: () => setExpanded((v) => !v),
                  style: {
                    background: "transparent",
                    border: "none",
                    color: linkColor,
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "4px 8px",
                    textDecoration: "underline"
                  },
                  children: expanded ? "Hide" : "Read more"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: onAgree,
                  style: {
                    padding: "8px 16px",
                    background: primaryColor,
                    color: buttonTextColor,
                    border: "none",
                    borderRadius: 4,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  },
                  children: buttonLabel
                }
              )
            ] }),
            expanded ? /* @__PURE__ */ jsx("div", { children: rendered }) : null
          ]
        }
      )
    }
  );
};
const AutoAcceptBannerLayout = ({
  agreement,
  textColor,
  linkColor,
  onAgree
}) => {
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || "", agreement, textColor, linkColor),
    [agreement, textColor, linkColor]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "region",
      "aria-label": agreement.title || "Agreement",
      style: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        background: "#fff",
        borderTop: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
        padding: "12px 16px",
        maxHeight: "60%",
        overflowY: "auto"
      },
      children: /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            maxWidth: 720,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 8
          },
          children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "flex-start", gap: 12 }, children: [
            /* @__PURE__ */ jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [
              agreement.title ? /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    fontSize: 14,
                    lineHeight: "20px",
                    color: textColor,
                    fontWeight: 600,
                    marginBottom: 4
                  },
                  children: agreement.title
                }
              ) : null,
              /* @__PURE__ */ jsx("div", { children: rendered })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: onAgree,
                style: {
                  background: "transparent",
                  border: "none",
                  color: linkColor,
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "4px 8px",
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                  flexShrink: 0
                },
                children: "Close"
              }
            )
          ] })
        }
      )
    }
  );
};
const ConciergeAgreementCard = ({
  agreement,
  pendingUserPrompt,
  themeColors,
  onAgree
}) => {
  const buttonLabel = agreement.agreeButtonLabel || "I agree";
  const textColor = (themeColors == null ? void 0 : themeColors.textPrimary) || "#111827";
  const primaryColor = (themeColors == null ? void 0 : themeColors.primary) || "#000";
  const buttonTextColor = (themeColors == null ? void 0 : themeColors.buttonText) || "#fff";
  const linkColor = "#0E7490";
  const layout = agreement.template || "inline";
  const layoutProps = {
    agreement,
    pendingUserPrompt,
    buttonLabel,
    textColor,
    primaryColor,
    buttonTextColor,
    linkColor,
    onAgree
  };
  if (layout === "centered-modal") return /* @__PURE__ */ jsx(CenteredModalLayout, { ...layoutProps });
  if (layout === "bottom-banner") return /* @__PURE__ */ jsx(BottomBannerLayout, { ...layoutProps });
  if (layout === "auto-accept") return /* @__PURE__ */ jsx(AutoAcceptBannerLayout, { ...layoutProps });
  return /* @__PURE__ */ jsx(InlineLayout, { ...layoutProps });
};
export {
  ConciergeAgreementCard
};
//# sourceMappingURL=ConciergeAgreementCard.js.map
